import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Card, TextField, FormControl, useMediaQuery, Button, Checkbox, FormControlLabel } from "@mui/material";
import Translate from "../utils/Translate";
import Companies from "../components/actors/Companies";
import Persons from "../components/actors/Persons";
import Preloader from "../components/Preloader";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import SpeedMenu from "../components/SpeedMenu";
import PersonCrud from "../components/actors/crud/PersonCrud";
import CompanyCrud from "../components/actors/crud/CompanyCrud";
import DuplicateActorMerger from "../components/actors/duplicates/DuplicateActorMerger";
import { actorSearch, actorCompanyTypes, actorPersonTypes } from "../utils/actorSearch";
import { hasSomeAccess, accessKeys, hasAccess } from "../utils/userAccess";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";

const filterableFields = ["actorId", "dataId", "parentActorId"];
const scrollSessionStorageKey = "Scroll_Actors";
const personsPaginationSessionStorageKey = "PaginationModel_Actors_Persons";
const companiesPaginationSessionStorageKey = "PaginationModel_Actors_Companies";

function saveScrollPosition() {
  sessionStorage.setItem(scrollSessionStorageKey, window.scrollY.toString());
}

function restoreScrollPosition() {
  var scrollPosition = sessionStorage[scrollSessionStorageKey]
    ? parseInt(sessionStorage[scrollSessionStorageKey])
    : null;
  if (scrollPosition) {
    window.scrollTo(0, scrollPosition);
  }
}

function handleNavigatedToActor() {
  // We set this so that if the user returns back from the actor we will know,
  // and then we can retore the previous scroll position
  window.history.replaceState(null, "", `${window.location.pathname}#return`);
  saveScrollPosition();
}

function handleNavigatedBackFromActor(isReturn) {
  if (isReturn) {
    // Remove #return from url
    window.history.replaceState(null, "", window.location.pathname);

    // Restore scroll position when the window resizes after search results update UI
    const observer = new ResizeObserver(() => {
      if (document.body.scrollHeight > window.innerHeight) {
        restoreScrollPosition();
        observer.disconnect();
      }
    });
    observer.observe(document.body);
  }
}

const getSpeedMenu = (props) => {
  const menudata = [
    ...(hasSomeAccess(accessKeys.standardPackage, accessKeys.advancedPackage)
      ? [
          {
            icon: <PeopleAltIcon />,
            text: Translate.get("ManageDuplicates"),
            route: false,
            dialogHeader: Translate.get("MergeDuplicates"),
            component: <DuplicateActorMerger {...props} />,
          },
        ]
      : []),
    {
      icon: <BusinessIcon />,
      text: Translate.get("AddCompany"),
      component: <CompanyCrud {...props} isEditOnly />,
    },
    {
      icon: <PersonIcon />,
      text: Translate.get("AddPerson"),
      route: false,
      component: <PersonCrud {...props} isEditOnly />,
    },
  ];
  return menudata;
};

const SearchField = ({ inputRef }) => {
  const [query, setQuery] = useState("");

  return (
    <TextField
      inputRef={inputRef}
      inputProps={{ spellCheck: false }}
      fullWidth
      id="outlined-basic"
      label={Translate.get("SearchNameOrCompany")}
      variant="outlined"
      name="searchString"
      value={query}
      onChange={(evt) => setQuery(evt.target.value)}
    />
  );
};

const formatSearchedPersons = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }

  // Filtering out some actorGroups here instead of backend, because in other
  // cases we might still want these connections from actor search, and we
  // still need to include them in the search, to be able to find all actors.
  return data
    .filter((x) => !x.actorTypes.includes(1))
    .map((x) => ({
      ...x,
      actorTypes: x.actorTypes.filter((a) => a === 3 || a === 4 || a === 6),
      parentActors: x.parentActors.filter(
        (a) =>
          (a.actorType === 3 || a.actorType === 4 || a.actorType === 6) &&
          a.parentActor.actorId > 0 &&
          a.parentActor.actorId !== x.actorId &&
          a.parentActor.actorName !== "ePortMaster",
      ),
    }));
};

function extractFilters(searchStr) {
  if (!searchStr) {
    return { newSearchStr: searchStr, filters: {} };
  }

  const words = searchStr.split(" ");
  const newSearchStr = words.filter((w) => !w.includes(":") || !filterableFields.includes(w.split(":")[0])).join(" ");
  const filters = Object.fromEntries(
    words.filter((w) => w.includes(":") && filterableFields.includes(w.split(":")[0])).map((f) => f.split(":")),
  );
  return { newSearchStr, filters };
}

const Comp = (props) => {
  const smallScreen = useMediaQuery("(max-width:700px)");
  const [rows, setData] = useState([]);
  const [rowsC, setDataC] = useState([]);
  const [dataFetched, setDataFetched] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasStoredSearch] = useState(!!(window.global.dataGrid.actorSearch || window.global.dataGrid.actorIdSearch));
  const [isReturn] = useState(window.location.hash?.startsWith("#return"));
  const [showInactivePersons, setShowInactivePersons] = useState(false);
  const [showInactiveCompanies, setShowInactiveCompanies] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const searchFieldRef = useRef();

  const activePersonRows = useMemo(() => rows.filter((r) => !r.isInactive), [rows]);
  const inactivePersonRows = useMemo(() => rows.filter((r) => r.isInactive), [rows]);
  const activeCompanyRows = useMemo(() => rowsC.filter((r) => !r.isInactive), [rowsC]);
  const inactiveCompanyRows = useMemo(() => rowsC.filter((r) => r.isInactive), [rowsC]);

  const searchActors = useCallback(async (searchStr, searchId) => {
    setHasSearched(true);
    setShowInactivePersons(false);
    setShowInactiveCompanies(false);
    const { newSearchStr, filters } = extractFilters(searchStr);
    const actorId = !!searchId ? searchId : "actorId" in filters ? filters.actorId : null;
    let personsSearch;
    let companySearch;
    if (actorId) {
      window.global.dataGrid.actorIdSearch = actorId;
      window.global.dataGrid.actorSearch = null;
      personsSearch = actorSearch(actorId, actorPersonTypes, ["ActorId"], 1, true);
      companySearch = actorSearch(actorId, actorCompanyTypes, ["ActorId"], 1, true);
    } else {
      window.global.dataGrid.actorIdSearch = null;
      window.global.dataGrid.actorSearch = searchStr;
      const searchFilters = { ...filters, includeInactive: "true" };
      personsSearch = actorSearch(
        newSearchStr,
        actorPersonTypes,
        [
          "ActorName",
          "OrgNo",
          "ActorEmail",
          "ParentActorName",
          "ExternalActorNo",
          "PostalAddressCity",
          "ActorPhoneNumber",
        ],
        500,
        false,
        searchFilters,
      );
      companySearch = actorSearch(
        newSearchStr,
        actorCompanyTypes,
        ["ActorId", "ActorName", "OrgNo", "ActorEmail", "ExternalActorNo", "ExternalDataId", "PostalAddressCity"],
        500,
        false,
        searchFilters,
      );
    }

    setDataFetched(false);
    const personResult = await personsSearch;
    const companyResult = await companySearch;

    if (personResult.isSuccessful === false || personResult.isSuccessful === false) {
      const translationKey =
        personResult.errorMessageTranslationKey || companyResult.errorMessageTranslationKey || "SomethingFailed";
      const errorMessage = enqueueSnackbar(Translate.get(translationKey), {
        variant: "error",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(errorMessage),
      });
    }

    setData(Array.isArray(personResult) ? formatSearchedPersons(personResult) : []);
    setDataC(Array.isArray(companyResult) ? companyResult : []);
    setDataFetched(true);
  }, []);

  useEffect(() => {
    async function init() {
      if (!hasSearched && hasStoredSearch) {
        await searchActors(window.global.dataGrid.actorSearch, window.global.dataGrid.actorIdSearch);
        handleNavigatedBackFromActor(isReturn);
      }
    }

    init();
  }, [hasSearched, hasStoredSearch, isReturn, searchActors]);

  const handleActorCreated = (actor) => {
    // Because of the page reload after a dialog closes, the state "search" will be re-initialized
    // to false. So searchActors will be called above then. We just need to set the ID here.
    window.global.dataGrid.actorIdSearch = actor.actorId;
  };

  //Form control
  const handleSubmit = (event) => {
    window.global.dataGrid.Companies = false;
    window.global.dataGrid.Users = false;
    searchActors(searchFieldRef.current.value, null);
    event.preventDefault();
  };
  //Form control

  useEffect(() => {
    // autofocus property on textfield did not work, what to do ;)
    const timer = setTimeout(() => searchFieldRef.current.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {!dataFetched && <Preloader />}
      <div className="stdFlexRowTop">
        <h2>{Translate.get("Register")}</h2>
        <FormControl sx={{ marginLeft: "8px" }} fullWidth>
          <form className="actorsSearchForm" onSubmit={handleSubmit}>
            <SearchField inputRef={searchFieldRef} />
            {!smallScreen && (
              <Button type="submit" className="actorsSearchButton" variant="contained" endIcon={<SearchIcon />}>
                {Translate.get("Search")}
              </Button>
            )}
          </form>
        </FormControl>
      </div>
      <div className="stdFlexRow">
        <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
          <h2>{Translate.get("Persons")}</h2>
          {inactivePersonRows.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox checked={showInactivePersons} onChange={(e) => setShowInactivePersons(e.target.checked)} />
              }
              label={Translate.get("ShowInactive")}
            />
          )}
        </div>
      </div>
      <Card>
        <Persons
          rows={showInactivePersons ? inactivePersonRows : activePersonRows}
          smallScreen={smallScreen}
          noRowsTransKey={Translate.get("EmptyHereRegister")}
          onNavigatedToActor={handleNavigatedToActor}
          paginationSessionStorageKey={personsPaginationSessionStorageKey}
          restorePaginationSession={hasStoredSearch && isReturn}
        />
      </Card>
      <div className="stdFlexRow">
        <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
          <h2>{Translate.get("Company")}</h2>
          {inactiveCompanyRows.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInactiveCompanies}
                  onChange={(e) => setShowInactiveCompanies(e.target.checked)}
                />
              }
              label={Translate.get("ShowInactive")}
            />
          )}
        </div>
      </div>
      <Card>
        <Companies
          rows={showInactiveCompanies ? inactiveCompanyRows : activeCompanyRows}
          smallScreen={smallScreen}
          noRowsTransKey={Translate.get("EmptyHereRegister")}
          onNavigatedToActor={handleNavigatedToActor}
          paginationSessionStorageKey={companiesPaginationSessionStorageKey}
          restorePaginationSession={hasStoredSearch && isReturn}
        />
      </Card>
      <SpeedMenu
        content={getSpeedMenu({
          actorId: 0,
          onActorSaved: handleActorCreated,
        })}
      />
    </>
  );
};
export default Comp;
